import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { LanguageSelector } from "../components/LanguageSelector";
import { 
  Leaf, 
  ArrowLeft,
  ArrowRight,
  Building2,
  Shield,
  FileCheck,
  Users,
  Landmark,
  GraduationCap,
  Heart,
  Globe,
  CheckCircle,
  Mail,
  Phone,
  Sparkles,
  Lock,
  BarChart3,
  FileText
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Entreprises() {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    organization: "",
    type: "",
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/demo/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: `[INSTITUTION] ${formData.organization} (${formData.type})`,
          employees: "institutional",
          message: formData.message
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success(t('demo.success.title'));
      } else {
        throw new Error("Error");
      }
    } catch (error) {
      console.log("Institutional request:", formData);
      setIsSubmitted(true);
      toast.success(t('demo.success.title'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Shield, key: "compliance" },
    { icon: Globe, key: "transparency" },
    { icon: BarChart3, key: "efficiency" },
    { icon: Lock, key: "security" }
  ];

  const sectors = [
    { icon: Landmark, key: "collectivities" },
    { icon: Building2, key: "administrations" },
    { icon: Heart, key: "health" },
    { icon: GraduationCap, key: "education" }
  ];

  const features = [
    { icon: Users, key: "multiSite" },
    { icon: FileText, key: "customReports" },
    { icon: FileCheck, key: "audit" }
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <Card className="max-w-lg w-full border-0 shadow-xl">
          <CardContent className="p-12 text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              {t('demo.success.title')}
            </h1>
            <p className="text-muted-foreground text-lg">
              {t('demo.success.message')}{' '}
              <strong className="text-foreground">{t('demo.success.timeframe')}</strong>.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button variant="outline" className="rounded-full px-6">
                  {t('demo.success.backHome')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('common.back')}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 hero-gradient">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Landmark className="w-4 h-4" />
                {t('entreprises.badge')}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6" style={{ fontFamily: 'Manrope' }}>
                {t('entreprises.title')}{' '}
                <span className="text-gradient">{t('entreprises.titleHighlight')}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0">
                {t('entreprises.description')}
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl" />
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/c9494d0d-73ab-44f8-9e0a-94701bb6dc88/images/677c07273d757e229588d3c3102473f45f4922cdfb273ca65a1638cd13233593.png"
                alt="Institutions publiques durables"
                className="relative rounded-3xl shadow-xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12" style={{ fontFamily: 'Manrope' }}>
            {t('entreprises.benefits.title')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <Card key={benefit.key} className="border-0 shadow-md text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                    {t(`institutions.benefits.${benefit.key}`)}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t(`institutions.benefits.${benefit.key}Desc`)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12" style={{ fontFamily: 'Manrope' }}>
            {t('entreprises.sectors.title')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sectors.map((sector) => (
              <Card key={sector.key} className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <sector.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                    {t(`institutions.sectors.${sector.key}`)}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t(`institutions.sectors.${sector.key}Desc`)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12" style={{ fontFamily: 'Manrope' }}>
            {t('entreprises.features.title')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature) => (
              <Card key={feature.key} className="border-0 shadow-md">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                      {t(`institutions.features.${feature.key}`)}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t(`institutions.features.${feature.key}Desc`)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-xl" data-testid="institutional-form">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl flex items-center justify-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Sparkles className="w-6 h-6 text-primary" />
                {t('entreprises.contact.title')}
              </CardTitle>
              <CardDescription>
                {t('entreprises.contact.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="organization">{t('entreprises.contact.form.organization')} *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      required
                      className="pl-10"
                      data-testid="inst-organization"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">{t('entreprises.contact.form.type')} *</Label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="inst-type"
                  >
                    <option value="">{t('entreprises.contact.form.selectType')}</option>
                    <option value="collectivity">{t('entreprises.contact.form.collectivity')}</option>
                    <option value="administration">{t('entreprises.contact.form.administration')}</option>
                    <option value="health">{t('entreprises.contact.form.health')}</option>
                    <option value="education">{t('entreprises.contact.form.education')}</option>
                    <option value="other">{t('entreprises.contact.form.other')}</option>
                  </select>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('entreprises.contact.form.name')} *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="inst-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('entreprises.contact.form.email')} *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="pl-10"
                        data-testid="inst-email"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('entreprises.contact.form.phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="inst-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t('entreprises.contact.form.message')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="resize-none"
                    data-testid="inst-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full rounded-full"
                  disabled={isSubmitting}
                  data-testid="inst-submit"
                >
                  {isSubmitting ? t('common.loading') : t('entreprises.contact.form.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-card border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Leaf className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-foreground">VF Decide</span>
          </div>
          <p className="text-sm text-muted-foreground">{t('landing.footer.copyright')}</p>
        </div>
      </footer>
    </div>
  );
}
